
import { useRef, useEffect, useState, useCallback } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { HeadingBlockConfiguration } from '@/types/block-configurations';
import { buildTextDecoration, buildFontStyle } from '@/lib/email-builder/text-style-utils';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';
import { stripHtmlTags } from '@/lib/html-sanitizer';

export function HeadingBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as HeadingBlockConfiguration;

  const patchBlockConfigLocal = useEmailBuilderStore((s) => s.patchBlockConfigLocal);
  const persistBlockConfig = useEmailBuilderStore((s) => s.persistBlockConfig);
  const contentRef = useRef<HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Text the DOM currently shows. A different config.text means the change came from elsewhere
  // (panel, undo, reload) and has to be written into the DOM.
  const shownTextRef = useRef<string | undefined>(undefined);

  // Typing updates the store at once (so the panel and undo see the current text) and the server
  // after a pause; the latest configuration is read when sending.
  const { debouncedUpdate, immediateUpdate } = useDebouncedUpdate({
    delay: 500,
    onUpdate: () => {
      persistBlockConfig(block.id).catch((error) => {
        console.error('Failed to save heading text:', error);
      });
    },
  });

  // Show changes made outside this editor (and the initial content)
  useEffect(() => {
    if (!contentRef.current || config.text === shownTextRef.current) return;
    shownTextRef.current = config.text;
    const newContent = config.text || 'Heading Text';
    if (contentRef.current.textContent !== newContent) {
      contentRef.current.textContent = newContent;
    }
  }, [config.text]);

  // Changing the level recreates the element, which starts out empty
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.textContent = config.text || 'Heading Text';
      shownTextRef.current = config.text;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.level]);

  // Handle content changes (heading text is plain text only)
  const handleInput = useCallback(() => {
    if (contentRef.current) {
      const plainText = stripHtmlTags(contentRef.current.textContent || '');
      shownTextRef.current = plainText;
      patchBlockConfigLocal(block.id, { text: plainText });
      debouncedUpdate(plainText);
    }
  }, [block.id, debouncedUpdate, patchBlockConfigLocal]);

  // Handle paste events - strip all HTML for headings
  const handlePasteEvent = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text && contentRef.current) {
      // Insert plain text only
      document.execCommand('insertText', false, text);
      handleInput();
    }
  }, [handleInput]);

  // Leaving the editor sends any edit still waiting
  const handleBlur = useCallback(() => {
    setIsEditing(false);
    immediateUpdate();
  }, [immediateUpdate]);

  // Handle double-click to enter edit mode
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    // Focus the contentEditable element
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
        // Select all text on double-click
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }, 0);
  }, []);

  // Handle single-click - select block but don't edit
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(block.id);
  }, [block.id, onSelect]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Exit edit mode on Escape or Enter
    if (e.key === 'Escape' || e.key === 'Enter') {
      e.preventDefault();
      contentRef.current?.blur();
    }
    // Save on Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      immediateUpdate();
    }
  }, [immediateUpdate]);

  // Determine which heading element to render
  const HeadingTag = config.level;

  const fontSize = config.typography?.fontSize || 24;

  // MJML renders headings inside mj-text. The td/container carries the padding;
  // the heading element itself carries only typographic styles + the browser-default
  // margin computed as the UA-stylesheet em-factor × the element's own font-size.
  const headingMarginEmFactors: Record<string, number> = {
    h1: 0.67,
    h2: 0.83,
    h3: 1.00,
    h4: 1.33,
    h5: 1.67,
    h6: 2.33,
  };
  const marginEm = headingMarginEmFactors[config.level] ?? 0.83;
  const marginPx = `${(marginEm * fontSize).toFixed(3)}px`;

  // Container div → mirrors mj-text's <td> which owns the padding
  const containerStyle: React.CSSProperties = {
    paddingTop: `${config.padding?.top ?? 10}px`,
    paddingRight: `${config.padding?.right ?? 25}px`,
    paddingBottom: `${config.padding?.bottom ?? 10}px`,
    paddingLeft: `${config.padding?.left ?? 25}px`,
  };

  // Heading element → mirrors the <h1-h6> inside mj-text's wrapper div
  const headingProps = {
    className: `outline-none ${isEditing ? 'cursor-text' : ''}`,
    contentEditable: isEditing,
    suppressContentEditableWarning: true,
    onInput: handleInput,
    onPaste: handlePasteEvent,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    style: {
      color: config.typography?.color || '#000000',
      fontSize: `${fontSize}px`,
      fontFamily: config.typography?.fontFamily || 'Arial, sans-serif',
      fontWeight: config.typography?.fontWeight || 'bold',
      fontStyle: buildFontStyle(config.typography),
      lineHeight: config.typography?.lineHeight || 1.3,
      textAlign: config.alignment?.horizontal || 'left',
      // Backend wraps the heading in an <a> when a link is set — hint that visually
      textDecoration:
        config.link?.enabled && config.link.url
          ? 'underline'
          : buildTextDecoration(config.typography),
      textTransform: config.typography?.textTransform || 'none',
      letterSpacing: config.typography?.letterSpacing ? `${config.typography.letterSpacing}px` : 'normal',
      // margin proportional to the element's own font-size, matching browser UA defaults
      margin: `${marginPx} 0`,
      padding: 0,
      minHeight: '20px', // Ensure block is visible even with no content
    } as React.CSSProperties,
  };

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onHover(block.id);
      }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-heading cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
        ${isEditing ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
      `}
      title={isEditing ? 'Editing - Press Escape or Enter to finish' : 'Double-click to edit heading'}
    >
      {/* mj-text container: padding lives here, not on the heading element */}
      <div style={containerStyle}>
        {HeadingTag === 'h1' && <h1 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
        {HeadingTag === 'h2' && <h2 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
        {HeadingTag === 'h3' && <h3 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
        {HeadingTag === 'h4' && <h4 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
        {HeadingTag === 'h5' && <h5 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
        {HeadingTag === 'h6' && <h6 ref={contentRef as React.RefObject<HTMLHeadingElement>} {...headingProps} />}
      </div>
    </div>
  );
}
